/**
 * Local-calendar date math for the inline DateRangeFilter.
 * @layer util
 * @package @plerity/ui-core
 *
 * Everything works in the user's LOCAL calendar and serializes to 'YYYY-MM-DD'.
 * The legacy dashboard-shared picker built its ranges with
 * `Date#toISOString().slice(0, 10)` and parsed them with `new Date('YYYY-MM-DD')`,
 * both of which are UTC: in the evening in Mountain time "today" was already
 * tomorrow, and a "Jan 1 - Jan 28" label rendered as "Dec 31 - Jan 27".
 * Pure functions (no React, no I/O); pass `today` to pin the clock in tests.
 */

export interface IsoRange {
  start: string;
  end: string;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

const pad = (n: number): string => String(n).padStart(2, "0");

/** Format a Date as 'YYYY-MM-DD' in the local calendar. */
export function toLocalIso(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Parse 'YYYY-MM-DD' (extra time text is ignored) as local midnight.
 * Returns null for anything that is not a real calendar date.
 */
export function parseLocalIso(iso: string | null | undefined): Date | null {
  const match = iso ? ISO_DATE.exec(iso) : null;
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(year, month - 1, day);
  // Reject overflow like 2025-02-31 (JS would roll it into March).
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return date;
}

/** `date` shifted by whole calendar days (DST-safe: uses setDate, not milliseconds). */
export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/** Inclusive window of `days` days ending on `today`. */
export function lastDaysRange(days: number, today: Date = new Date()): IsoRange {
  return { start: toLocalIso(addDays(today, -(days - 1))), end: toLocalIso(today) };
}

/** January 1 of `today`'s year through `today`. */
export function yearToDateRange(today: Date = new Date()): IsoRange {
  return { start: `${today.getFullYear()}-01-01`, end: toLocalIso(today) };
}

/**
 * The period of identical length that ends the day before `start`
 * (Jan 1-28 compares to Dec 4-31). Returns null when either bound is invalid.
 */
export function previousPeriod(start: string, end: string): IsoRange | null {
  const s = parseLocalIso(start);
  const e = parseLocalIso(end);
  if (!s || !e) return null;
  const lengthDays = Math.round((e.getTime() - s.getTime()) / 86_400_000) + 1;
  const compEnd = addDays(s, -1);
  return { start: toLocalIso(addDays(compEnd, -(lengthDays - 1))), end: toLocalIso(compEnd) };
}

/** "Jan 1 - Jan 28, 2025" (year on the end date only). Empty string if either bound is invalid. */
export function formatRangeLabel(start: string, end: string, locale = "en-US"): string {
  const s = parseLocalIso(start);
  const e = parseLocalIso(end);
  if (!s || !e) return "";
  const short = (d: Date) => d.toLocaleDateString(locale, { month: "short", day: "numeric" });
  const long = (d: Date) => d.toLocaleDateString(locale, { month: "short", day: "numeric", year: "numeric" });
  return `${short(s)} - ${long(e)}`;
}

/** Clamp an ISO date into [min, max]; either bound may be omitted. Invalid input is returned unchanged. */
export function clampIso(iso: string, min?: string, max?: string): string {
  if (!parseLocalIso(iso)) return iso;
  if (min && iso < min) return min;
  if (max && iso > max) return max;
  return iso;
}
